/**
 * 出门便签上的字（用户 10-03：TA 在屋里、院子里不贴纸条，只有出去玩的时候门口放一张；便签写 TA 真实去了哪）。
 * - 和地图首页读同一份世界状态（同一个查询键：地图读过就直接用缓存），用同一套说法（world_map/copy 的 panelCopy），只说接口给的事实；
 * - 便签是 TA 留的，所以第一人称：“团子在回家的路上”写成“我在回家的路上”；第二行只留前两段、各占一行（“往西湖去 / 还要 13 分钟”，窄屏不会把“工钱”拆在两行），全文在地图上；
 * - 只在 TA 在外面、live、知道是哪个账号时读；还没读到、读不到、世界状态里 TA 在家或还没同步到，就返回 null，便签照旧“我出门啦”。
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { HomeSnapshot, WorldState } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { serverNow } from "@/shared/time/clock";
import { panelCopy } from "@/features/world_map/copy";
import { legProgress } from "@/features/world_map/model";
import { sceneFromWorldState } from "@/features/world_map/worldState";
import { petIsAway, type AwayNote } from "./HomeScene";

/** 便签上“还要几分钟”多久跟着变一次 */
const TICK_MS = 30_000;
/** 地图面板几段之间的连接（copy.ts joinParts：不断行空格＋“· ”） */
const PART_JOIN = `${String.fromCharCode(0xa0)}· `;

export function awayNoteOf(state: WorldState, petId: string, nowMs: number): AwayNote | null {
  const pet = sceneFromWorldState(state).pets.find((p) => p.petId === petId);
  if (!pet) return null;
  const phase = pet.activity.phase;
  if (phase !== "going" && phase !== "there" && phase !== "returning") return null;
  const copy = panelCopy(pet, nowMs, phase !== "there" && pet.leg ? legProgress(pet.leg, nowMs) : null);
  const [first, ...rest] = copy.headlineParts;
  if (!first?.startsWith(pet.name)) return null;
  const title = `我${first.slice(pet.name.length)}${rest.join("")}`;
  const detail = copy.detail ? copy.detail.split(PART_JOIN).slice(0, 2).join("\n") : null;
  return { title, detail };
}

/** 只在 on 时每 TICK_MS 走一次的“现在”（在家时不起定时器） */
function useTick(on: boolean): number {
  const [now, setNow] = useState(serverNow);
  useEffect(() => {
    if (!on) return;
    setNow(serverNow());
    const timer = window.setInterval(() => setNow(serverNow()), TICK_MS);
    return () => window.clearInterval(timer);
  }, [on]);
  return now;
}

export function useAwayNote(snapshot: HomeSnapshot): AwayNote | null {
  const { world } = useServices();
  const userId = useOptionalCurrentHousehold()?.userId ?? null;
  const petId = snapshot.pet.pet_id;
  const on = petIsAway(snapshot) && env.dataMode === "live" && Boolean(userId);
  const state = useQuery<WorldState>({
    // 和地图首页同一个键（只按用户）：地图刚读过就不再读
    queryKey: queryKeys.worldStateFor(userId ?? "-"),
    queryFn: ({ signal }) => world.state(petId, signal),
    enabled: on,
    staleTime: 15_000,
    refetchInterval: on ? 60_000 : false,
  });
  const now = useTick(on);
  return on && state.data ? awayNoteOf(state.data, petId, now) : null;
}
